import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChessHttpClient, type GameStateResponse, type PlayerColor } from '@chess/core';
import { Board, parseFenTurn } from './Board';
import { getGameOverMessage } from './gameOverMessage';
import { shouldShowGameBoard } from './gameVisibility';

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.DEV ? 'http://localhost:3000' : window.location.origin);
const STORAGE_KEY_GAME_ID = 'chess_game_id';
const STORAGE_KEY_ROOM_CODE = 'chess_room_code';
const STORAGE_KEY_TOKEN = 'chess_token';
const STORAGE_KEY_COLOR = 'chess_color';
const POLL_INTERVAL_MS = Number(import.meta.env.VITE_POLL_INTERVAL_MS) || 45000;

export function App() {
  const [gameId, setGameId] = useState<string | null>(null);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [color, setColor] = useState<PlayerColor | null>(null);
  const [copied, setCopied] = useState(false);
  const [gameState, setGameState] = useState<GameStateResponse | null>(null);

  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);

  const lastMoveNumberRef = useRef(0);

  const client = useMemo(() => new ChessHttpClient(API_URL), []);

  const clearGameState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY_GAME_ID);
      localStorage.removeItem(STORAGE_KEY_ROOM_CODE);
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_COLOR);
    } catch (error) {
      console.warn('Failed to clear stored game state:', error);
    }
    setGameId(null);
    setRoomCode(null);
    setToken(null);
    setColor(null);
    setGameState(null);
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearGameState();
    setSessionExpiredMessage('Game session expired. Enter room code to continue.');
  }, [clearGameState]);

  const handleBackToMenu = useCallback(() => {
    clearGameState();
    setSessionExpiredMessage(null);
  }, [clearGameState]);

  useEffect(() => {
    if (!gameId || !token) return;

    let cancelled = false;

    client
      .getGameState(gameId, token)
      .then(response => {
        if (!cancelled) setGameState(response);
      })
      .catch(error => {
        if (error instanceof Error && error.message === 'Token expired') {
          if (!cancelled) handleSessionExpired();
          return;
        }
        console.error('Failed to fetch game state:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, token, client, handleSessionExpired]);

  useEffect(() => {
    if (gameState) lastMoveNumberRef.current = gameState.move_count;
  }, [gameState?.move_count]);

  useEffect(() => {
    if (!gameId || !token) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval>;

    const stopPolling = () => clearInterval(intervalId);

    const poll = async () => {
      try {
        const response = await client.pollForMoves(gameId, lastMoveNumberRef.current, token);
        if (cancelled) return;

        if (response.moves.length > 0) {
          const latest = response.moves[response.moves.length - 1];
          lastMoveNumberRef.current = latest.move_number;
          setGameState(prev => ({
            fen: latest.fen_result,
            turn: parseFenTurn(latest.fen_result),
            move_count: latest.move_number,
            status: response.game_status,
            last_move: latest.move_type === 'move' ? { from: latest.from_square, to: latest.to_square } : prev?.last_move ?? null,
            result: response.result,
            result_reason: response.result_reason,
            draw_offered_by: response.draw_offered_by,
          }));
        } else {
          setGameState(prev =>
            prev
              ? {
                  ...prev,
                  status: response.game_status,
                  result: response.result,
                  result_reason: response.result_reason,
                  draw_offered_by: response.draw_offered_by,
                }
              : prev
          );
        }

        if (response.game_status === 'finished' || response.game_status === 'abandoned') {
          stopPolling();
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'Token expired') {
          stopPolling();
          if (!cancelled) handleSessionExpired();
          return;
        }
        console.error('Polling failed:', error);
      }
    };

    intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [gameId, token, client, handleSessionExpired]);

  useEffect(() => {
    try {
      const storedGameId = localStorage.getItem(STORAGE_KEY_GAME_ID);
      const storedRoomCode = localStorage.getItem(STORAGE_KEY_ROOM_CODE);
      const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
      const storedColor = localStorage.getItem(STORAGE_KEY_COLOR);

      if (storedGameId && storedRoomCode && storedToken && (storedColor === 'white' || storedColor === 'black')) {
        setGameId(storedGameId);
        setRoomCode(storedRoomCode);
        setToken(storedToken);
        setColor(storedColor);
      }
    } catch (error) {
      // localStorage may be unavailable in private browsing mode
      console.warn('Failed to restore game state from localStorage:', error);
    }
  }, []);

  const persistGameState = (gameId: string, roomCode: string, token: string, color: PlayerColor) => {
    try {
      localStorage.setItem(STORAGE_KEY_GAME_ID, gameId);
      localStorage.setItem(STORAGE_KEY_ROOM_CODE, roomCode);
      localStorage.setItem(STORAGE_KEY_TOKEN, token);
      localStorage.setItem(STORAGE_KEY_COLOR, color);
    } catch (storageError) {
      console.warn('Failed to save room data to localStorage:', storageError);
    }
  };

  const handleCreateRoom = async () => {
    setCreating(true);
    setCreateError(null);
    setSessionExpiredMessage(null);

    try {
      const response = await client.createRoom();
      setGameId(response.game_id);
      setRoomCode(response.room_code);
      setToken(response.white_token);
      setColor('white');
      persistGameState(response.game_id, response.room_code, response.white_token, 'white');
    } catch (error) {
      console.error('Failed to create room:', error);
      setCreateError('Failed to create game. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinRoom = async (event: FormEvent) => {
    event.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (!code) return;

    setJoining(true);
    setJoinError(null);
    setSessionExpiredMessage(null);

    try {
      const response = await client.joinRoom(code);
      setGameId(response.game_id);
      setRoomCode(code);
      setToken(response.black_token);
      setColor('black');
      persistGameState(response.game_id, code, response.black_token, 'black');
    } catch (error) {
      if (error instanceof Error && error.message === 'Room not found') {
        setJoinError('Room not found. Check the code and try again.');
      } else if (error instanceof Error && error.message === 'Room already has two players') {
        setJoinError('This room already has two players.');
      } else {
        setJoinError('Failed to join room. Please try again.');
      }
    } finally {
      setJoining(false);
    }
  };

  const handleMoveApplied = ({ fen, from, to }: { fen: string; from: string; to: string }) => {
    setGameState(prev =>
      prev
        ? {
            ...prev,
            fen,
            turn: parseFenTurn(fen),
            move_count: prev.move_count + 1,
            last_move: { from, to },
          }
        : prev
    );
  };

  const handleGameStateUpdate = (update: Partial<GameStateResponse>) => {
    setGameState(prev => (prev ? { ...prev, ...update } : prev));
  };

  const handleCopyCode = async () => {
    if (roomCode) {
      try {
        await navigator.clipboard.writeText(roomCode);
        setCopied(true);
      } catch (error) {
        console.error('Failed to copy code:', error);
      }
    }
  };

  useEffect(() => {
    if (copied) {
      const timerId = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timerId);
    }
  }, [copied]);

  if (!gameId) {
    return (
      <div style={{ padding: '40px 20px', fontFamily: 'system-ui', textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '20px' }}>Chess Correspondence</h1>
        <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '30px' }}>Play chess at your own pace</p>
        {sessionExpiredMessage && (
          <p style={{ color: '#cc0000', fontSize: '0.9rem', marginBottom: '20px' }}>{sessionExpiredMessage}</p>
        )}
        <button
          onClick={handleCreateRoom}
          disabled={creating}
          style={{
            padding: '15px 30px',
            fontSize: '1.1rem',
            backgroundColor: creating ? '#99c2e8' : '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: creating ? 'default' : 'pointer',
            fontWeight: 'bold',
            marginBottom: createError ? '10px' : '30px',
          }}
        >
          {creating ? 'Creating...' : 'Create New Game'}
        </button>
        {createError && (
          <p style={{ color: '#cc0000', fontSize: '0.9rem', marginBottom: '20px' }}>{createError}</p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '320px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
          <span style={{ padding: '0 12px', color: '#999', fontSize: '0.9rem' }}>or</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
        </div>

        <form onSubmit={handleJoinRoom} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '320px' }}>
          <input
            type="text"
            data-testid="join-code-input"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder="Enter room code"
            maxLength={4}
            style={{
              padding: '12px 16px',
              fontSize: '1.5rem',
              fontFamily: 'monospace',
              letterSpacing: '0.1em',
              textAlign: 'center',
              textTransform: 'uppercase',
              border: '2px solid #0066cc',
              borderRadius: '8px',
              width: '100%',
              marginBottom: '15px',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            disabled={joining || !joinCode.trim()}
            style={{
              padding: '15px 30px',
              fontSize: '1.1rem',
              backgroundColor: joining || !joinCode.trim() ? '#99c2e8' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: joining || !joinCode.trim() ? 'default' : 'pointer',
              fontWeight: 'bold',
              width: '100%',
            }}
          >
            {joining ? 'Joining...' : 'Join Game'}
          </button>
          {joinError && (
            <p style={{ color: '#cc0000', fontSize: '0.9rem', marginTop: '15px' }}>{joinError}</p>
          )}
        </form>
      </div>
    );
  }

  const isGameVisible = Boolean(color && token && shouldShowGameBoard(color, gameState));

  if (isGameVisible && color && token) {
    const gameOverMessage = gameState
      ? getGameOverMessage(gameState.status, gameState.result, gameState.result_reason, color)
      : null;

    return (
      <div style={{ padding: '40px 20px', fontFamily: 'system-ui', textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '20px' }}>Game Started!</h1>
        {gameOverMessage && (
          <div
            data-testid="game-over-message"
            style={{
              backgroundColor: '#fff8e6',
              border: '2px solid #ff9800',
              borderRadius: '8px',
              padding: '16px 24px',
              marginBottom: '20px',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              color: '#a15c00',
            }}
          >
            {gameOverMessage}
          </div>
        )}
        <Board
          playerColor={color}
          turn={gameState?.turn ?? 'white'}
          status={gameState?.status ?? 'waiting'}
          fen={gameState?.fen}
          gameId={gameId}
          token={token}
          client={client}
          drawOfferedBy={gameState?.draw_offered_by}
          onMoveApplied={handleMoveApplied}
          onGameStateUpdate={handleGameStateUpdate}
        />
        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '20px' }}>Game ID: {gameId}</p>

        {gameState?.status === 'finished' && (
          <button
            onClick={handleBackToMenu}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              fontSize: '0.9rem',
              backgroundColor: 'transparent',
              color: '#0066cc',
              border: '2px solid #0066cc',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            ← Back to Main Menu
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 20px', fontFamily: 'system-ui', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '40px' }}>Your Game is Ready!</h1>

      <div style={{
        backgroundColor: '#f0f8ff',
        border: '2px solid #0066cc',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
      }}>
        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Share this code with your friend
        </p>

        <div
          onClick={handleCopyCode}
          data-testid="room-code"
          style={{
            backgroundColor: 'white',
            border: '3px solid #0066cc',
            borderRadius: '8px',
            padding: '30px 20px',
            marginBottom: '20px',
            fontSize: '3rem',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            letterSpacing: '0.1em',
            color: '#0066cc',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
        >
          {roomCode}
        </div>

        <button
          onClick={handleCopyCode}
          style={{
            padding: '10px 20px',
            fontSize: '0.95rem',
            backgroundColor: copied ? '#28a745' : '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            width: '100%',
            transition: 'background-color 0.2s',
          }}
        >
          {copied ? '✓ Copied!' : 'Copy Code'}
        </button>
      </div>

      <div style={{
        marginTop: '40px',
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#fff8e6',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '100%',
      }}>
        <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ff6b00', margin: '0' }}>
          ⏳ Waiting for opponent...
        </p>
        <p style={{ fontSize: '0.9rem', color: '#666', margin: '10px 0 0 0' }}>
          Your friend needs to enter the code above to join the game
        </p>
      </div>

      <div style={{ marginTop: '40px', fontSize: '0.85rem', color: '#999', textAlign: 'center' }}>
        <p>Game ID: {gameId}</p>
        <p>Token saved locally for reconnection</p>
      </div>

      <button
        onClick={handleBackToMenu}
        style={{
          marginTop: '20px',
          padding: '10px 20px',
          fontSize: '0.9rem',
          backgroundColor: 'transparent',
          color: '#0066cc',
          border: '2px solid #0066cc',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 'bold',
        }}
      >
        ← Back to Main Menu
      </button>
    </div>
  );
}
